import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router";

import {
  createDefaultConfig,
  createEmptySeries,
  deserializeConfig,
  EXPLORER_CONFIG_PARAM,
  EXPLORER_VIEW_PARAM,
  ExplorerConfig,
  ExplorerSeriesConfig,
  serializeConfig,
} from "@/pages/metricsExplorer/explorerConfig.ts";

interface UseExplorerConfig {
  config: ExplorerConfig;
  setConfig: (next: ExplorerConfig) => void;
  /** The saved chart a link asked to open, or null when the URL carries a config instead. */
  requestedViewId: number | null;
  /** Take on a saved chart's config and drop the request that asked for it. */
  adoptRequestedView: (next: ExplorerConfig) => void;
  /** Drop the request without changing the chart, for one that cannot be opened. */
  dismissRequestedView: () => void;
  updateSeries: (ref: string, patch: Partial<ExplorerSeriesConfig>) => void;
  addSeries: () => void;
  removeSeries: (ref: string) => void;
  duplicateSeries: (ref: string) => void;
}

/**
 * Keeps the whole chart definition in one search param.
 *
 * Holding it in the URL rather than component state is what makes a chart shareable and
 * bookmarkable, and makes browser back step through edits, without any server object.
 * Date range lives in its own params, owned by the date picker.
 */
export const useExplorerConfig = (): UseExplorerConfig => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(EXPLORER_CONFIG_PARAM);

  const config = useMemo(() => deserializeConfig(raw) ?? createDefaultConfig(), [raw]);

  const setConfig = useCallback(
    (next: ExplorerConfig) => {
      // Functional form: the date picker writes its own params, and a plain object would
      // drop whichever update lost the race.
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          updated.set(EXPLORER_CONFIG_PARAM, serializeConfig(next));
          return updated;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const requestedViewId = useMemo(() => {
    const raw = searchParams.get(EXPLORER_VIEW_PARAM);
    const parsed = Number(raw);
    return raw && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const adoptRequestedView = useCallback(
    (next: ExplorerConfig) => {
      // One update rather than setConfig() followed by a delete: setSearchParams does not
      // queue, so the second call would be computed from the same params as the first and
      // throw its work away.
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          updated.set(EXPLORER_CONFIG_PARAM, serializeConfig(next));
          updated.delete(EXPLORER_VIEW_PARAM);
          return updated;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const dismissRequestedView = useCallback(() => {
    setSearchParams(
      (previous) => {
        const updated = new URLSearchParams(previous);
        updated.delete(EXPLORER_VIEW_PARAM);
        return updated;
      },
      {replace: true}
    );
  }, [setSearchParams]);

  const updateSeries = useCallback(
    (ref: string, patch: Partial<ExplorerSeriesConfig>) => {
      setConfig({
        ...config,
        series: config.series.map((series) => (series.ref === ref ? {...series, ...patch} : series)),
      });
    },
    [config, setConfig]
  );

  const addSeries = useCallback(() => {
    setConfig({...config, series: [...config.series, createEmptySeries()]});
  }, [config, setConfig]);

  const removeSeries = useCallback(
    (ref: string) => {
      const remaining = config.series.filter((series) => series.ref !== ref);
      // The rail is never empty: an empty builder gives the user nothing to act on.
      setConfig({...config, series: remaining.length > 0 ? remaining : [createEmptySeries()]});
    },
    [config, setConfig]
  );

  const duplicateSeries = useCallback(
    (ref: string) => {
      const source = config.series.find((series) => series.ref === ref);
      if (!source) {
        return;
      }
      const copy = {...source, ref: createEmptySeries().ref};
      const index = config.series.findIndex((series) => series.ref === ref);
      const next = [...config.series];
      next.splice(index + 1, 0, copy);
      setConfig({...config, series: next});
    },
    [config, setConfig]
  );

  return {
    config,
    setConfig,
    requestedViewId,
    adoptRequestedView,
    dismissRequestedView,
    updateSeries,
    addSeries,
    removeSeries,
    duplicateSeries,
  };
};
